import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiConfiguration } from '../api-configuration';
import { BaseService } from '../base-service';
import {
  InterestedCustomerRegisterPayload,
  InterestedCustomerReportFilters,
} from '../models/interested-customers-manual.models';
import {
  ZamalekApplicationContent,
  ZamalekApplicationMeta,
  ZamalekApplicationPayload,
  ZamalekApplicationResult,
  ZamalekFormLinks
} from '../models/zamalek-academy-application-manual.models';

interface ApiEnvelope<T> {
  status: 'success' | 'error';
  data: T;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class InterestedCustomersManualService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  getPublicForm(slug: string): Observable<unknown> {
    return this.http.get(
      `${this.rootUrl}/api/v1/public/interested-customers/${encodeURIComponent(slug)}`
    );
  }

  register(slug: string, payload: InterestedCustomerRegisterPayload): Observable<unknown> {
    return this.http.post(
      `${this.rootUrl}/api/v1/public/interested-customers/${encodeURIComponent(slug)}`,
      payload,
      { headers: { 'X-Skip-Global-Error': 'true' } }
    );
  }

  getZamalekApplicationMeta(slug: string): Observable<ApiEnvelope<ZamalekApplicationMeta>> {
    return this.http.get<ApiEnvelope<ZamalekApplicationMeta>>(
      `${this.rootUrl}/api/v1/zamalek-academy/application-form/meta`,
      { params: { academy_slug: slug } }
    );
  }

  getZamalekApplicationContent(
    slug: string,
    type: ZamalekApplicationContent['type']
  ): Observable<ApiEnvelope<ZamalekApplicationContent>> {
    return this.http.get<ApiEnvelope<ZamalekApplicationContent>>(
      `${this.rootUrl}/api/v1/zamalek-academy/application-form/content`,
      { params: { academy_slug: slug, type } }
    );
  }

  getZamalekFormLinks(slug: string): Observable<ApiEnvelope<ZamalekFormLinks>> {
    return this.http.get<ApiEnvelope<ZamalekFormLinks>>(
      `${this.rootUrl}/api/v1/zamalek-academy/form-links`,
      { params: { academy_slug: slug } }
    );
  }

  submitZamalekApplication(
    payload: ZamalekApplicationPayload
  ): Observable<ApiEnvelope<ZamalekApplicationResult>> {
    return this.http.post<ApiEnvelope<ZamalekApplicationResult>>(
      `${this.rootUrl}/api/v1/zamalek-academy/application-form`,
      payload,
      { headers: { 'X-Skip-Global-Error': 'true' } }
    );
  }

  getParentRegistrationForm(slug: string): Observable<unknown> {
    return this.http.get(
      `${this.rootUrl}/api/v1/public/parent-registration/${encodeURIComponent(slug)}`
    );
  }

  registerParent(slug: string, payload: unknown): Observable<unknown> {
    return this.http.post(
      `${this.rootUrl}/api/v1/public/parent-registration/${encodeURIComponent(slug)}`,
      payload,
      { headers: { 'X-Skip-Global-Error': 'true' } }
    );
  }

  getParentRegistrationInfo(academyId: number): Observable<unknown> {
    return this.http.get(`${this.rootUrl}/api/v1/academy/${academyId}/public-parent-registration`);
  }

  report(academyId: number, filters: InterestedCustomerReportFilters = {}): Observable<unknown> {
    return this.http.get(
      `${this.rootUrl}/api/v1/academy/${academyId}/reports/interested-customers`,
      { params: this.buildParams(filters as Record<string, unknown>) }
    );
  }

  exportReport(
    academyId: number,
    filters: InterestedCustomerReportFilters = {}
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.rootUrl}/api/v1/academy/${academyId}/reports/interested-customers/export`, {
      params: this.buildParams(filters as Record<string, unknown>),
      observe: 'response',
      responseType: 'blob',
    });
  }

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return httpParams;
  }
}
